import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import { SkeletonRows, ErrorNotice, InfoNotice } from '../ui/States';
import { dayLabel } from '../../lib/events';
import { errorMessage, inputClass } from '../../lib/programs';
import type { EventNote, PanelProps } from './types';

/** Issues on one day (day set) or notes for next year (no day). Admins only. */
export default function NotesPanel({ detail, mutable, day }: PanelProps) {
  const [text, setText] = useState('');
  const key = day ?? 'next';
  const notes = useQuery<EventNote[]>(
    ['event-notes', detail.event.id, key],
    async () => (await api.get(`/events/${detail.event.id}/notes`, { params: { day: key } })).data.data,
    { enabled: mutable },
  );
  const add = useMutation(async () => api.post(`/events/${detail.event.id}/notes`, { day: day ?? null, text }), {
    onSuccess: () => {
      setText('');
      notes.refetch();
    },
  });
  return (
    <Panel>
      <PanelHeader title={day ? `Notes and issues, ${dayLabel(day)}` : 'Notes for next year'} />
      <PanelBody className="text-table">
        {!mutable ? (
          <InfoNotice>Notes are kept by admins.</InfoNotice>
        ) : (
          <>
            <div className="flex gap-2">
              <textarea
                className={`${inputClass} flex-1`}
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={
                  day ? 'What happened, what to fix (never banking details)' : 'What to keep or change next year'
                }
              />
              <Button size="sm" disabled={!text.trim() || add.isLoading} onClick={() => add.mutate()}>
                Add
              </Button>
            </div>
            {add.error ? (
              <div className="mt-2">
                <ErrorNotice>{errorMessage(add.error, 'Could not add the note.')}</ErrorNotice>
              </div>
            ) : null}
            {notes.isLoading ? (
              <SkeletonRows rows={2} />
            ) : notes.error ? (
              <ErrorNotice>Could not load the notes.</ErrorNotice>
            ) : !notes.data?.length ? (
              <p className="text-ink-2 mt-3">No notes yet.</p>
            ) : (
              <ul className="mt-3">
                {notes.data.map((n) => (
                  <li key={n.id} className="border-b border-line py-2">
                    <div className="whitespace-pre-wrap">{n.text}</div>
                    <div className="text-small text-ink-2 mt-1">
                      {n.by ?? 'an operator'} ·{' '}
                      {new Date(n.at).toLocaleString('en-CA', {
                        timeZone: 'America/Toronto',
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </PanelBody>
    </Panel>
  );
}

export function NextYearNotes(props: PanelProps) {
  return <NotesPanel {...props} day={undefined} />;
}
